import React from 'react';
import { useEffect, useState, useRef, useMemo } from 'react';

import { Table, Tab, Tabs, Button, Modal, Form, Col, Row, Stack, ListGroup } from 'react-bootstrap';

import { Formik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import Loader from '../helper/Loader';
import useConfirmation from '../helper/Confirmation';

import { formatDate, formatTime } from '../helper/DateTimeFormater';
import useRequest from '../helper/useRequest';

import { date, timeStart, timeEnd, intervalTime } from '../helper/Schema';

import MandatoryFieldNotice from '../helper/MandatoryFieldNotice';

const schema = {
    Date: date.test("checkDate", "Es kann kein Termin an einem Sonntag stattfinden.", (value) => { return value.getDay() > 0; }),
    TimeStart: timeStart,
    TimeEnd: timeEnd,
    IntervalTime: intervalTime,
};

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'

const columnHelper = createColumnHelper();

function Appointment() {
    const confirmation = useConfirmation();
    const { request } = useRequest();

    const [sorting, setSorting] = useState([]);
    const [addAppointment, setAddAppointment] = useState(false);
    const [message, setMessage] = useState("");
    const [appointment, setAppointment] = useState(false);
    const [appointments, setAppointments] = useState(false);

    const submit = async (values) => {
        if (appointment !== false) {
            return editAppointment(values);
        }
        try {
            let result = await request("/Operator/AddAppointment", values);
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
                }
            }
            else {
                toast.success("Termin wurde erfolgreich erstellt.");
                setAppointments((old) => [...old, json.appointmentObj]);
                setAddAppointment(false);
            }
        } catch (ex) {
            toast.error(ex.message);
        }
    };

    const openEditAppointment = (app) => {
        setAppointment(app);
        setAddAppointment(true);
    }

    const editAppointment = async (values) => {
        try {
            let result = await request("/Operator/UpdateAppointment", { ...appointment, ...values });
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
                }
            }
            else {
                toast.success("Termin wurde erfolgreich gespeichert.");
                setAppointments((old) => old.map(i => {
                    if (i.AppointmentID == json.appointment.AppointmentID) {
                        return { ...i, Appointment: json.appointment.Appointment, TimeStart: json.appointment.TimeStart, TimeEnd: json.appointment.TimeEnd, IntervalTime: json.appointment.IntervalTime }
                    }

                    return i;
                }));
                setAddAppointment(false);
            }
        } catch (ex) {
            toast.error(ex.message);
        }
    }

    const deleteAppointment = async (app) => {
        try {
            const id = app.AppointmentID;
            let result = await request("/Operator/DeleteAppointment", {AppointmentID: app.AppointmentID});
            let json = result.data;
            if (json.r != 1) {
                if (json.message) {
                    toast.error(json.message);
                }
                else {
                    toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
                }
            }
            else {
                toast.success("Termin wurde erfolgreich gelöscht.");
                setAppointments((old) => old.filter(i => i.AppointmentID != id));
                setAddAppointment(false);
            }
        } catch (ex) {
            toast.error(ex.message);
        }
    }

    const getAppointmentCountOpen = (app) => {
        let t1 = new Date("1970-01-01 " + app.TimeStart);
        let t2 = new Date("1970-01-01 " + app.TimeEnd);
        let totalMinutes = t2.getHours() * 60 - t1.getHours() * 60;
        totalMinutes += t2.getMinutes() - t1.getMinutes();
        
        return Math.floor(totalMinutes / app.IntervalTime);
    }

    useEffect(() => {
        const getAppointments = async () => {
            try {
                let result = await request("/Operator/GetAppointments");
                let json = result.data;
                if (json.r != 1) {
                    if (json.message) {
                        toast.error(json.message);
                    }
                    else {
                        toast.error("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
                    }
                }
                else {
                    setAppointments(json.appointments);
                }
            }
            catch (ex) {
                toast.error("Laden der Termine fehlgeschlagen.");
            }
        };

        getAppointments();
    }, []);

     

    const columns = [
        columnHelper.accessor('Appointment', {
            header: () => <span>Datum</span>,
            cell: info => formatDate(info.getValue()),
            enableColumnFilter: false,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('TimeStart', {
            header: () => <span>Zeit</span>,
            cell: info => {
                return <div>{formatTime(info.row.original.TimeStart)} - {formatTime(info.row.original.TimeEnd)}</div>;
            },
            enableColumnFilter: false,
            enableSorting: false,
        }),
        columnHelper.accessor('IntervalTime', {
            header: () => <span>Intervall</span>,
            cell: info => info.getValue(),
            enableColumnFilter: false,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('TimeEnd', {
            header: () => <span>Termine</span>,
            cell: info => getAppointmentCountOpen(info.row.original),
            enableColumnFilter: false,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('Bookings', {
            header: () => <span>Gebuchte Termine</span>,
            cell: info => info.row.original.Bookings ? info.row.original.Bookings.length : 0,
            enableColumnFilter: false,
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('OrganisationID', {
            header: () => <span></span>,
            cell: info => {
                return <div className="text-end">
                    <Button size="sm" variant="primary" className="me-1" onClick={() => openEditAppointment(info.row.original)}>Details</Button>
                    <Button size="sm" variant="danger" onClick={() => confirmation.openModal("Wirklich löschen?", "Möchten Sie diesen Termin wirklich löschen?", () => { deleteAppointment(info.row.original) }, "Ja", "Nein")} disabled={info.row.original.Bookings && info.row.original.Bookings.length > 0}>Löschen</Button>
                </div>;
            },
            enableSorting: false,
            enableColumnFilter: false,
        }),
    ]

    const t = useReactTable({
        data: appointments || [],
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    if (appointments === false) return <Loader />

    return (
        <>
            <div className="text-end">
                <button className="btn btn-primary" onClick={() => { setAddAppointment(true); setAppointment(false);  }}>Termin hinzufügen</button>
            </div>
            <AppointmentModal modal={addAppointment} closeModal={() => { setAddAppointment(false); }} message={message} submit={submit} appointment={appointment} />

            <Table striped>
                <thead>
                    {t.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
                                return (
                                    <th key={header.id} style={{ whiteSpace: "nowrap" }}>
                                        {header.isPlaceholder
                                            ? null
                                            : (
                                                <div {...{
                                                    className: header.column.getCanSort()
                                                        ? 'cursor-pointer select-none'
                                                        : '',
                                                    onClick: header.column.getToggleSortingHandler(),
                                                }}>
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                    {{
                                                        asc: <i className="fa-solid fa-sort-up ms-2"></i>,
                                                        desc: <i className="fa-solid fa-sort-down ms-2"></i>,
                                                    }[header.column.getIsSorted()] ?? (header.column.getCanSort() ? <i className="fa-solid fa-sort ms-2"></i> : null)}
                                                </div>
                                            )
                                        }
                                    </th>
                                )
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {t.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                            {row.getVisibleCells().map(cell => {
                                return (
                                    <td key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            </Table>
        </>
    );
}

const AppointmentModal = ({ modal, closeModal, message, submit, appointment }) => {
    const formRef = useRef();
    const [disabled, setDisabled] = useState(false);

    useEffect(() => {
        setDisabled(false);

        if (!formRef.current) return;
        if (!appointment) {
            formRef.current.setFieldValue("Date", "");
            formRef.current.setFieldValue("TimeStart", "");
            formRef.current.setFieldValue("TimeEnd", "");
            formRef.current.setFieldValue("IntervalTime", "");
            return;
        }

        formRef.current.setFieldValue("Date", appointment.Appointment);
        formRef.current.setFieldValue("TimeStart", formatTime(appointment.TimeStart));
        formRef.current.setFieldValue("TimeEnd", formatTime(appointment.TimeEnd));
        formRef.current.setFieldValue("IntervalTime", appointment.IntervalTime);
        
        if (appointment.Bookings && appointment.Bookings.length > 0) {
            setDisabled(true);
        }

    }, [appointment]);

    const appointmentList = useMemo(() => {
        if (!appointment) return null;

        let t1 = new Date("1970-01-01 " + appointment.TimeStart);
        let t2 = new Date("1970-01-01 " + appointment.TimeEnd);
        let totalMinutes = t2.getHours() * 60 - t1.getHours() * 60;
        totalMinutes += t2.getMinutes() - t1.getMinutes();

        let count = Math.floor(totalMinutes / appointment.IntervalTime);

        let startTime = new Date("1970-01-01 " + appointment.TimeStart);
        let nextTime;
        let items = [];
        for (let i = 0; i < count; i++) {
            nextTime = new Date(startTime.getTime() + 1000 * 60 * appointment.IntervalTime);

            let foundSlot = false;
            if (appointment.Bookings) {
                foundSlot = appointment.Bookings.find(i => formatTime(i.Time) == formatTime(startTime));
            }
            if (foundSlot) {
                let item = <ListGroup.Item key={i}>
                    <div className="d-flex justify-content-between">
                        <span style={{ textDecoration: "line-through" }}>Termin {i + 1} ({formatTime(startTime)} - {formatTime(nextTime)})</span>
                        <span>{foundSlot.FirstName} {foundSlot.LastName} ({foundSlot.Email})</span>
                    </div>
                </ListGroup.Item>
                items.push(item)
            }
            else {
                items.push(<ListGroup.Item key={i}>Termin {i + 1} ({formatTime(startTime)} - {formatTime(nextTime)})</ListGroup.Item>)
            }
            
            startTime = nextTime;
        }

        return (
            <>
                <h4 className="mt-2">Mögliche Termine</h4>
                <ListGroup>
                    {items}
                </ListGroup>
            </>
        )
    }, [appointment]);

    return (
        <Modal show={modal} onHide={closeModal}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {appointment && "Termin bearbeiten"}
                    {!appointment && "Termin hinzufügen"}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {message && <Alert type="error" message={message} />}

                <Formik
                    innerRef={(f) => (formRef.current = f)}
                    validationSchema={yup.object().shape(schema)}
                    onSubmit={(values) => {
                        submit(values);
                    }}
                    initialValues={{
                        Date: '',
                        TimeStart: '',
                        TimeEnd: '',
                        IntervalTime: 45,
                    }}
                >
                    {({
                        handleSubmit,
                        handleChange,
                        handleBlur,
                        values,
                        touched,
                        isValid,
                        errors,
                    }) => {
                        return (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Stack gap={2}>
                                    <Form.Group controlId="Date">
                                        <Form.Label className="required">Datum</Form.Label>
                                        <Form.Control isInvalid={errors.Date && touched.Date} disabled={disabled} type="date" placeholder="" name="Date" value={values.Date} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.Date}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId="TimeStart">
                                        <Form.Label className="required">Anfangszeit</Form.Label>
                                        <Form.Control isInvalid={errors.TimeStart && touched.TimeStart} disabled={disabled} type="time" placeholder="" name="TimeStart" value={values.TimeStart} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.TimeStart}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId="TimeEnd">
                                        <Form.Label className="required">Endzeit</Form.Label>
                                        <Form.Control isInvalid={errors.TimeEnd && touched.TimeEnd} disabled={disabled} type="time" placeholder="" name="TimeEnd" value={values.TimeEnd} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.TimeEnd}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId="IntervalTime">
                                        <Form.Label className="required">Intervall-Zeit</Form.Label>
                                        <Form.Control isInvalid={errors.IntervalTime && touched.IntervalTime} disabled={disabled} type="number" placeholder="" name="IntervalTime" value={values.IntervalTime} onBlur={handleBlur} onChange={handleChange} />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.IntervalTime}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Stack>

                                <MandatoryFieldNotice />
                            </Form>
                        )
                    }}
                </Formik>

                {appointmentList}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    Abbrechen
                </Button>
                <Button variant="primary" onClick={() => formRef.current.submitForm()}>
                    {appointment && "Speichern"}
                    {!appointment && "Hinzufügen"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default Appointment;
